import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { CkHelpProvider, HelpPanel, searchHelpArticles } from "@NP-Webber/crm-kit/dist/next/index.js";
import type { CkPaletteSection } from "@NP-Webber/crm-kit/dist/next/index.js";
import { EVENTS, onEvent, openDesignTour, openHelpCenter } from "../events";
import { useShell } from "../shell/ShellContext";
import { articleRoutesMatch, HELP_ARTICLES, HELP_CATEGORIES } from "./helpArticles";

/** "מדריכים" in the Ctrl+K palette */
export function helpPaletteSection(query: string): CkPaletteSection | null {
  const q = query.trim();
  if (!q) return null;
  const found = searchHelpArticles(HELP_ARTICLES, q, HELP_CATEGORIES).slice(0, 4);
  if (!found.length) return null;
  return {
    title: "מדריכים",
    items: found.map((a) => ({
      key: `h-${a.slug}`,
      label: a.title,
      sub: "מדריך",
      icon: "book",
      onSelect: () => openHelpCenter(a.slug),
    })),
  };
}

/**
 * HelpCenter — the in-app help center (mounted once by the shell around the page content).
 * Opens from the "?" beside every screen title, the top bar, F1, Ctrl+K results and the personal menu.
 * "במסך הזה" = guides whose routes match the current path.
 */
export function HelpCenter({ children, onReportBug }: { children: ReactNode; onReportBug?: () => void }) {
  const location = useLocation();
  const { current } = useShell();
  const [open, setOpen] = useState(false);
  const [openSlug, setOpenSlug] = useState<string | null>(null);

  const show = useCallback((slug?: string | null) => {
    setOpenSlug(slug || null);
    setOpen(true);
  }, []);

  useEffect(() => {
    const off = onEvent<{ slug?: string | null }>(EVENTS.help, (d) => show(d?.slug));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "F1" && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        show(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      off();
      window.removeEventListener("keydown", onKey);
    };
  }, [show]);

  // leaving the screen closes the panel (its context would be stale)
  useEffect(() => setOpen(false), [location.pathname]);

  const contextSlugs = useMemo(
    () => HELP_ARTICLES.filter((a) => articleRoutesMatch(a, location.pathname)).map((a) => a.slug),
    [location.pathname],
  );

  const loadArticle = useCallback(async (slug: string) => {
    const a = HELP_ARTICLES.find((x) => x.slug === slug);
    if (!a) throw new Error("המדריך לא נמצא");
    return { ...a, bodyHtml: a.bodyHtml, related: a.related ?? [] };
  }, []);

  const helpValue = useMemo(
    () => ({ available: true, open: (slug?: string | null) => show(slug), screenHasGuides: contextSlugs.length > 0 }),
    [show, contextSlugs.length],
  );

  const actions = [
    {
      key: "tour",
      label: "סיור מודרך",
      sub: "דקה על העיצוב החדש",
      icon: "sparkle",
      onClick: () => {
        setOpen(false);
        openDesignTour();
      },
    },
    onReportBug
      ? {
          key: "bug",
          label: "דיווח על תקלה",
          sub: "במרכז ההודעות",
          icon: "alert",
          onClick: () => {
            setOpen(false);
            onReportBug();
          },
        }
      : null,
  ].filter(Boolean) as Array<{ key: string; label: string; sub?: string; icon?: string; onClick: () => void }>;

  return (
    <CkHelpProvider value={helpValue}>
      {children}
      <HelpPanel
        open={open}
        onClose={() => setOpen(false)}
        articles={HELP_ARTICLES}
        categories={HELP_CATEGORIES}
        contextSlugs={contextSlugs}
        contextLabel={current?.label || ""}
        openSlug={openSlug}
        loadArticle={loadArticle}
        actions={actions}
      />
    </CkHelpProvider>
  );
}

export default HelpCenter;
